import {computed, ref} from 'vue';

import type {TId} from '@/assets/core/assets/api/types';
import type {IApplication, IApplicationFilter} from '@/api/types/application';
import type {ILayer} from '@/api/types/layer';
import {ELayerType} from '@/api/enums/layer';

import {applicationController} from '@/services/controllers/application';
import {layerController} from '@/services/controllers/layer';

export const useLayerCacheHook = () => {
  const cache = ref<Record<TId, ILayer>>({});
  const cacheLinkedApplications = ref<Record<TId, IApplication[]>>({});

  const getCache = computed(() => cache.value);
  const getCacheLinkedApplications = computed(
    () => cacheLinkedApplications.value,
  );

  const getCacheRasterLayers = computed(() => {
    const layers = Object.values(getCache.value).filter(
      (item) => item.type === ELayerType.RASTER,
    );

    return Object.fromEntries(layers.map((item) => [item.id, item]));
  });

  const getCacheVectorLayers = computed(() => {
    const layers = Object.values(getCache.value).filter(
      (item) => item.type === ELayerType.VECTOR,
    );

    return Object.fromEntries(layers.map((item) => [item.id, item]));
  });

  const getCacheVectorLayersFeatureStyle = computed(() => {
    return Object.fromEntries(
      Object.entries(getCacheVectorLayers.value).map(([key, value]) => [
        key,
        value.feature_styles,
      ]),
    );
  });

  const getCacheStreamLayers = computed(() => {
    const layers = Object.values(getCache.value).filter(
      (item) => item.type === ELayerType.STREAM,
    );

    return Object.fromEntries(layers.map((item) => [item.id, item]));
  });

  const getCacheStreamLayersFeatureStyle = computed(() => {
    return Object.fromEntries(
      Object.entries(getCacheStreamLayers.value).map(([key, value]) => [
        key,
        value.feature_styles,
      ]),
    );
  });

  const loadOne = async (id: TId): Promise<ILayer> => {
    cache.value[id] = await layerController.getOne(id);
    return cache.value[id];
  };

  const loadLinkedApplications = async (id?: TId): Promise<IApplication[]> => {
    const params: IApplicationFilter = id
      ? {layers_id: [id]}
      : {is_global: true};

    return await applicationController.list
      .loadAllPageWithCache(params)
      .then((data) => {
        cacheLinkedApplications.value[id ?? 'null'] = data;
        return data;
      })
      .catch(() => {
        cacheLinkedApplications.value[id ?? 'null'] = [];
        return [];
      });
  };

  const getOne = async (id: TId): Promise<ILayer> =>
    cache.value[id] ?? (await loadOne(id));

  const getLinkedApplications = async (id?: TId) => {
    return (
      cacheLinkedApplications.value[id ?? 'null'] ??
      (await loadLinkedApplications(id))
    );
  };

  return {
    cache,
    cacheLinkedApplications,

    getCache,
    getCacheRasterLayers,
    getCacheVectorLayers,
    getCacheVectorLayersFeatureStyle,
    getCacheStreamLayers,
    getCacheStreamLayersFeatureStyle,

    getCacheLinkedApplications,

    getLinkedApplications,
    getOne,
    loadOne,
  };
};

export const layerCache = useLayerCacheHook();
